import { useEffect, useRef, useState } from 'react';

import { useRevalidator } from '@remix-run/react';

/**
 * Refetch loader data at the moment a lot reaches time zero.
 *
 * Every product surface holds loader data captured at page load and runs a
 * one-second interval that re-renders the card from that frozen snapshot. The
 * countdown therefore ticks accurately while the data behind it never moves,
 * so when a lot hits its cutoff the card decides SOLD vs UNSOLD from a
 * `bidCount` that may be hours old. Bids placed after the page loaded are
 * invisible, and a lot that sold reads UNSOLD until the visitor refreshes.
 *
 * That is the defect the client reported as "system auto refreshed all the
 * time but wrongly, not that it cannot auto refresh", and why refreshing by
 * hand always showed the correct label.
 *
 * One timer per page, aimed at the next cutoff among the lots on screen — not
 * one timer per card. A staggered sale closing 155 lots a minute apart costs
 * one revalidation a minute; a sale closing every lot at the same instant
 * costs one revalidation, not 155.
 *
 * Soft close is handled for free: a bid in the final 60 seconds extends that
 * cutoff, the revalidation picks up the new value, and the next timer is
 * scheduled against it.
 */

/**
 * Never hold a timer further out than this.
 *
 * Covers the realistic case — open the catalogue in the morning for a sale
 * closing that evening — while excluding the 2099 sentinel that TIME2LIVE
 * auctions store. It also keeps the delay far below the signed 32-bit ceiling
 * on setTimeout, past which a timer fires immediately and would spin.
 */
const SCHEDULING_HORIZON_MS = 24 * 60 * 60 * 1000;

/**
 * Matches the delay auction-detail already applies after a countdown ends:
 * give the server a moment so the read does not race the write closing the
 * lot. Doubles as a floor, so clock skew cannot produce a tight loop.
 */
const SETTLE_MS = 1000;

type CutOff = Date | string | number | null | undefined;

const toTime = (value: CutOff): number | null => {
  if (value === null || value === undefined) return null;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * The soonest cutoff strictly after `now` and inside the horizon, or null when
 * there is nothing left to wait for. Exported for testing.
 */
export function nextLotCloseAt(
  cutOffTimes: ReadonlyArray<CutOff>,
  now: number,
  horizonMs: number = SCHEDULING_HORIZON_MS,
): number | null {
  let soonest: number | null = null;

  for (const value of cutOffTimes) {
    const time = toTime(value);
    if (time === null || time <= now || time - now > horizonMs) continue;
    if (soonest === null || time < soonest) soonest = time;
  }

  return soonest;
}

export function useRevalidateOnLotClose(cutOffTimes: ReadonlyArray<CutOff>): void {
  const revalidator = useRevalidator();

  // Keep the callback out of the effect deps; its identity is not guaranteed.
  const revalidate = useRef(revalidator.revalidate);
  revalidate.current = revalidator.revalidate;

  // Bumped when a timer fires, so the effect re-runs and schedules the lot
  // after this one. The cutoffs themselves are unchanged by a lot closing, so
  // without this the effect would never reschedule.
  const [pass, setPass] = useState(0);

  // A stable dependency: the cutoffs, not the array identity, which is rebuilt
  // on every render. Cheap at the ~28 lots a page renders.
  const cutOffKey = cutOffTimes.map((value) => toTime(value) ?? '').join(',');

  useEffect(() => {
    const now = Date.now();
    const times = cutOffKey.split(',').map((t) => (t === '' ? null : Number(t)));
    const closeAt = nextLotCloseAt(times, now);
    if (closeAt === null) return;

    const timer = setTimeout(
      () => {
        setPass((n) => n + 1);
        revalidate.current();
      },
      Math.max(closeAt - now + SETTLE_MS, SETTLE_MS),
    );

    return () => clearTimeout(timer);
  }, [cutOffKey, pass]);
}
